import React, { useEffect, useRef, useState } from "react"
import { Helmet } from "react-helmet"
import { useLocation } from "@reach/router"
import { graphql, useStaticQuery } from "gatsby"
import loadable from "@loadable/component"

import SEO from "../components/seo"
import PMPLayout from "../components/index-components/pmp-layout"
import PMPHeader from "../components/index-components/pmp-header"
import HeroVideo from "../components/about-us/hero-video"
import OurStorySection from "../components/about-us/our-story"

const OurCultureSection = loadable(
  () => import("../components/about-us/our-culture"),
  {
    fallback: <div style={{ minHeight: "400px" }} />,
  }
)

const LeadershipSection = loadable(
  () => import("../components/about-us/leadership"),
  {
    fallback: <div style={{ minHeight: "400px" }} />,
  }
)

const AboutUsPage = () => {
  const { pathname } = useLocation()
  const [headerTransparent, setHeaderTransparent] = useState(true)
  const ourStoryRef = useRef(null)

  const data = useStaticQuery(graphql`
    query {
      allWordpressAcfVideos {
        edges {
          node {
            acf {
              hero_video
              carousel_1
              carousel_2
              our_culture
            }
          }
        }
      }
    }
  `)

  // Function to extract video URL from HTML of WYSIWYG
  // WordPress WYSIWYG always returns videos in <source> tag format
  const extractVideoUrlFromHtml = html => {
    if (!html) return null
    const sourceSrcMatch = html.match(/<source[^>]+src=["']([^"']+)["']/i)
    return sourceSrcMatch ? sourceSrcMatch[1] : null
  }

  // Extract hero_video from WordPress GraphQL
  const heroVideoHtml =
    data?.allWordpressAcfVideos?.edges?.[0]?.node?.acf?.hero_video || null
  const heroVideoUrl =
    extractVideoUrlFromHtml(heroVideoHtml) ||
    "https://cdn.inpwrd.net/wp-content/uploads/2025/11/13031557/hero.mp4"

  useEffect(() => {
    if (typeof window === "undefined") return
    const el = ourStoryRef.current
    if (!el) return
    let ticking = false
    const BUFFER = 8
    const getHeaderHeight = () => {
      const nav = document.querySelector(".navbar")
      return nav ? Math.round(nav.getBoundingClientRect().height) : 64
    }
    const update = () => {
      ticking = false
      const h = getHeaderHeight()
      const top = Math.round(el.getBoundingClientRect().top)
      const shouldBeSolid = top <= h - BUFFER
      const shouldBeTransparent = top > h + BUFFER
      if (shouldBeSolid && headerTransparent) setHeaderTransparent(false)
      else if (shouldBeTransparent && !headerTransparent)
        setHeaderTransparent(true)
    }
    const onScrollOrResize = () => {
      if (!ticking) {
        ticking = true
        requestAnimationFrame(update)
      }
    }
    update()
    window.addEventListener("scroll", onScrollOrResize, { passive: true })
    window.addEventListener("resize", onScrollOrResize, { passive: true })
    return () => {
      window.removeEventListener("scroll", onScrollOrResize)
      window.removeEventListener("resize", onScrollOrResize)
    }
  }, [headerTransparent])

  return (
    <PMPLayout showNavigation>
      <SEO
        title="About inPowered - Our mission is to transform advertising with AI that drives outcomes for brands and value for people"
        description="Learn more about a startup on a mission to help brands maximize their Advertising ROI."
      />
      <Helmet defer={false}>
        <link
          rel="preconnect"
          href="https://cdn.inpwrd.net"
          crossOrigin="anonymous"
        />
        <link
          rel="preload"
          as="image"
          href="/videos/hero-poster.jpg"
          fetchpriority="high"
        />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, viewport-fit=cover"
        />
      </Helmet>

      <PMPHeader transparent={headerTransparent} />

      <HeroVideo
        key={pathname}
        poster="/videos/hero-poster.jpg"
        src={heroVideoUrl}
      />

      <div ref={ourStoryRef}>
        <OurStorySection />
      </div>

      <OurCultureSection />

      <LeadershipSection />
    </PMPLayout>
  )
}

export default AboutUsPage
